import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { listPlatformDominios } from "../../../api/client";
import { WorkspaceSegments } from "../../../constants/workspaceSegments";

function subscriptionStatusChipColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "trialing") return "info";
  if (s === "past_due") return "warning";
  if (s === "suspended" || s === "cancelled") return "error";
  return "default";
}

const WorkspacesTabPanel = () => {
  const { t } = useTranslation("app");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [domainSearch, setDomainSearch] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [noSubscription, setNoSubscription] = useState(false);
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [dominios, setDominios] = useState([]);

  const subscriptionStatusLabel = useCallback(
    (status) =>
      t(`billingPage.subscriptionStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: String(status || "-"),
      }),
    [t]
  );

  const fetchDominios = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 300 };
      const q = String(domainSearch || "").trim();
      if (q) params.q = q;
      if (subscriptionStatus) params.subscriptionStatus = subscriptionStatus;
      if (noSubscription) params.noSubscription = "1";
      if (includeExcluded) params.includeExcluded = "1";
      const res = await listPlatformDominios(params);
      const items = Array.isArray(res?.items) ? res.items : [];
      setDominios(items);
    } catch (err) {
      setError(err?.message || t("platformPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [domainSearch, subscriptionStatus, noSubscription, includeExcluded, t]);

  useEffect(() => {
    fetchDominios();
  }, []);

  const dominiosSorted = useMemo(() => {
    return [...dominios].sort((a, b) =>
      String(a.dominio || "").localeCompare(String(b.dominio || ""), undefined, { sensitivity: "base" })
    );
  }, [dominios]);

  const handleSearch = () => {
    fetchDominios();
  };

  const formatTs = useCallback((v) => {
    if (!v || String(v).startsWith("0000-00-00")) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  }, []);

  const segmentLabel = useCallback(
    (raw) => {
      const key = String(raw || "").trim() || WorkspaceSegments.Generic;
      return t(`platformPage.segmentOption.${key}`, { defaultValue: key });
    },
    [t]
  );

  return (
    <Box>
      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label={t("platformPage.domainsSearch")}
          value={domainSearch}
          onChange={(e) => setDomainSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label={t("platformPage.filterSubscriptionStatus")}
          value={subscriptionStatus}
          onChange={(e) => setSubscriptionStatus(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t("platformPage.filterAnyStatus")}</MenuItem>
          <MenuItem value="trialing">{subscriptionStatusLabel("trialing")}</MenuItem>
          <MenuItem value="active">{subscriptionStatusLabel("active")}</MenuItem>
          <MenuItem value="past_due">{subscriptionStatusLabel("past_due")}</MenuItem>
          <MenuItem value="suspended">{subscriptionStatusLabel("suspended")}</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={noSubscription}
              onChange={(e) => setNoSubscription(e.target.checked)}
              size="small"
            />
          }
          label={t("platformPage.filterNoSubscription")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeExcluded}
              onChange={(e) => setIncludeExcluded(e.target.checked)}
              size="small"
            />
          }
          label={t("platformPage.filterIncludeExcluded")}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          {t("platformPage.domainsApply")}
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t("platformPage.workspacesTableHint")}
      </Typography>

      <TableContainer variant="outlined" sx={{ maxHeight: 560, bgcolor: "background.paper" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("platformPage.colId")}</TableCell>
              <TableCell>{t("platformPage.colDominio")}</TableCell>
              <TableCell>{t("platformPage.colSegment")}</TableCell>
              <TableCell>{t("platformPage.colWorkspaceCreated")}</TableCell>
              <TableCell>{t("platformPage.colLastTenantLogin")}</TableCell>
              <TableCell>{t("platformPage.colPlan")}</TableCell>
              <TableCell>{t("platformPage.colSubscriptionStatus")}</TableCell>
              <TableCell align="right">{t("platformPage.colUsageIx30d")}</TableCell>
              <TableCell>{t("platformPage.colSignals")}</TableCell>
              <TableCell align="right">{t("platformPage.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dominios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? t("loading", { ns: "app", defaultValue: "Carregando..." }) : t("platformPage.domainsEmpty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dominiosSorted.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>{d.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{d.dominio}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {Number(d.excluido) !== 0 ? (
                        <Chip size="small" label={t("platformPage.badgeExcluded")} color="error" />
                      ) : null}
                      {Number(d.cron) ? (
                        <Chip size="small" variant="outlined" label={t("platformPage.chipCronSocial")} />
                      ) : null}
                      {Number(d.gerandoreceita) ? (
                        <Chip size="small" variant="outlined" color="success" label={t("platformPage.chipRevenue")} />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem" }}>
                    {d.workspace_segment != null && String(d.workspace_segment).trim() !== ""
                      ? segmentLabel(d.workspace_segment)
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>{formatTs(d.data_cadastro)}</TableCell>
                  <TableCell sx={{ fontSize: "0.8rem", maxWidth: 160 }} title={d.tenant_last_user_access_at || ""}>
                    {formatTs(d.tenant_last_user_access_at)}
                  </TableCell>
                  <TableCell>
                    {d.plan_sku ? (
                      <Typography variant="body2">
                        {d.plan_name || d.plan_sku}
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          {d.plan_sku} v{d.plan_version ?? "—"}
                        </Typography>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {d.subscription_status ? (
                      <Chip
                        size="small"
                        label={subscriptionStatusLabel(d.subscription_status)}
                        color={subscriptionStatusChipColor(d.subscription_status)}
                        variant="outlined"
                      />
                    ) : (
                      <Chip size="small" label={t("platformPage.noActiveSubscription")} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{Number(d.usage_interactions_30d || 0).toLocaleString()}</Typography>
                    {d.tenant_legacy_stats_ok && Number(d.usage_interactions_30d_billing || 0) !== Number(d.usage_interactions_30d || 0) ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        API: {Number(d.usage_interactions_30d_billing || 0).toLocaleString()}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(d.health_badges || []).map((b) => (
                        <Chip key={b} size="small" label={t(`platformPage.badge.${b}`, { defaultValue: b })} variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {Number(d.excluido) !== 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {t("platformPage.openWorkspaceExcluded")}
                      </Typography>
                    ) : (
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/platform/xprobase/workspaces/${d.id}`}
                        variant="outlined"
                      >
                        {t("platformPage.openWorkspace")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WorkspacesTabPanel;
